import React from 'react';
import styled from 'styled-components';
import { theme, media } from './theme';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import iplangold from './assets/graphics/iplangold.png';
import iplanrentier from './assets/graphics/iplanrentier.png';
import iplanmax from './assets/graphics/iplanmax.png';
import iplangoldsklitek from './assets/graphics/slitekgold.png';
import iplanrentiersklitek from './assets/graphics/slitekrentier.png';
import iplanmaxsklitek from './assets/graphics/slitekmax.png';
import ingoldpay from './assets/graphics/ingoldpaylogo.png';
import { ACCOUNT_TYPES } from './constants';
import {
  BaseContainer,
  BaseInner,
  BaseFormTitle,
  BaseAccountTypes,
  BaseGrid,
  BaseCard,
  BaseButton,
  BaseFeatureList,
  BaseAccountImage,
} from './components/shared/BaseAccountSelector';

// GoldenAccountsIntro-specific styled components
const TitleHeader = styled.div`
  font-weight: ${theme.fontWeights.bold};
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: row;
`;

const AccountTitle = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.gray300};
  margin-bottom: 0.25rem;
`;

const IGPImage = styled.img`
  height: auto;
  max-width: 140px;
`;

const AccountClient = styled.h3`
  font-weight: ${theme.fontWeights.semibold};
  font-size: ${theme.fontSizes.md};
  margin-bottom: 0.75rem;
`;

const GraphicImage = styled.img`
  width: 80px;
  object-fit: contain;

  ${media.mobile} {
    display: none;
  }
`;

/**
 * Props pro GoldenAccountsIntro komponentu
 */
interface GoldenAccountsIntroProps {
  /** Callback volaný při výběru účtu */
  onSelectPlan: (accountType: string) => void;
}

/**
 * Struktura pro popis jednoho zlatého účtu
 */
interface AccountInfo {
  typeKey: string;
  image: string;
  graphic: string;
  id: string;
}

const accountsData: readonly AccountInfo[] = [
  {
    typeKey: 'gold',
    image: iplangold,
    graphic: iplangoldsklitek,
    id: ACCOUNT_TYPES.GOLD,
  },
  {
    typeKey: 'rentier',
    image: iplanrentier,
    graphic: iplanrentiersklitek,
    id: ACCOUNT_TYPES.RENTIER,
  },
  {
    typeKey: 'max',
    image: iplanmax,
    graphic: iplanmaxsklitek,
    id: ACCOUNT_TYPES.MAX,
  },
] as const;

/**
 * Komponenta pro představení zlatých účtů s popisem typického klienta
 */
export default function GoldenAccountsIntro({ onSelectPlan }: GoldenAccountsIntroProps): React.ReactElement {
  const { t } = useTranslation(['accounts', 'common']);

  return (
    <BaseContainer>
      <BaseInner>
        <Header>
          <TitleHeader>{t('accounts:intro.header')}</TitleHeader>
          <BaseFormTitle>{t('accounts:intro.title')}</BaseFormTitle>
        </Header>

        <BaseAccountTypes>
          <BaseGrid>
            {accountsData.map((acc, i) => (
              <BaseCard key={i} onClick={() => onSelectPlan(acc.id)}>
                <CardHeader>
                  <div>
                    <AccountTitle>{t(`accounts:types.${acc.typeKey}.categoryTitle`)}</AccountTitle>
                    <BaseAccountImage src={acc.image} alt={t(`accounts:types.${acc.typeKey}.name`)} />
                    <IGPImage src={ingoldpay} alt="ingoldpay" />
                    <AccountClient>{t('accounts:intro.typicalClient')}</AccountClient>
                  </div>
                  <div>
                    <GraphicImage src={acc.graphic} alt={t(`accounts:types.${acc.typeKey}.name`)} />
                  </div>
                </CardHeader>
                <BaseFeatureList>
                  {(t(`accounts:types.${acc.typeKey}.features`, { returnObjects: true }) as string[]).map((f, j) => (
                    <li key={j}>{f}</li>
                  ))}
                </BaseFeatureList>
                <BaseButton>{t('common:buttons.explore')}</BaseButton>
              </BaseCard>
            ))}
          </BaseGrid>
        </BaseAccountTypes>
      </BaseInner>
    </BaseContainer>
  );
}
